/**
 * Validation utilities for form inputs and data validation
 * Reusable validation functions to maintain consistency across the application
 */

/**
 * Email validation regex pattern
 * Validates basic email format: user@domain.extension
 *
 * Pattern breakdown:
 * - ^[^\s@]+ : Start with one or more characters that are not whitespace or @
 * - @ : Must contain exactly one @ symbol
 * - [^\s@]+ : Domain part (no whitespace or @)
 * - \. : Must contain a dot
 * - [^\s@]+ : Extension part (no whitespace or @)
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone validation regex pattern
 * Allows optional + prefix followed by digits only
 */
export const PHONE_REGEX = /^\+?\d*$/;

/**
 * Validates if a string is a valid email format (strict validation)
 *
 * @param value - The string to validate
 * @param options - Validation options
 * @param options.allowEmpty - If true, empty strings are considered valid (default: true)
 * @returns true if valid, false otherwise
 *
 * @example
 * ```ts
 * isValidEmail('user@example.com') // true
 * isValidEmail('invalid.email') // false
 * isValidEmail('') // true (by default)
 * isValidEmail('', { allowEmpty: false }) // false
 * ```
 */
export function isValidEmail(
  value: string,
  options: { allowEmpty?: boolean } = { allowEmpty: true },
): boolean {
  // Allow empty string while typing if allowEmpty is true
  if (value === "") return options.allowEmpty ?? true;

  return EMAIL_REGEX.test(value);
}

/**
 * Validates email input while typing (permissive validation)
 * Blocks only obviously invalid characters (spaces, multiple @, etc.)
 * Use this for real-time input validation, and isValidEmail for final validation
 *
 * @param value - The string to validate
 * @returns true if the input is allowed, false otherwise
 *
 * @example
 * ```ts
 * isValidEmailInput('u') // true (allow typing)
 * isValidEmailInput('user@') // true (allow typing)
 * isValidEmailInput('user @domain') // false (no spaces)
 * isValidEmailInput('user@@domain') // false (no multiple @)
 * ```
 */
export function isValidEmailInput(value: string): boolean {
  // Allow empty string
  if (value === "") return true;

  // Block spaces
  if (/\s/.test(value)) return false;

  // Block multiple @ symbols
  if ((value.match(/@/g) || []).length > 1) return false;

  // Allow everything else while typing
  return true;
}

/**
 * Validates if a string is a valid phone number format
 *
 * @param value - The string to validate
 * @param options - Validation options
 * @param options.allowEmpty - If true, empty strings are considered valid (default: true)
 * @returns true if valid, false otherwise
 *
 * @example
 * ```ts
 * isValidPhone('+50312345678') // true
 * isValidPhone('12345678') // true
 * isValidPhone('abc123') // false
 * isValidPhone('') // true (by default)
 * ```
 */
export function isValidPhone(
  value: string,
  options: { allowEmpty?: boolean } = { allowEmpty: true },
): boolean {
  if (value === "") return options.allowEmpty ?? true;

  return PHONE_REGEX.test(value);
}

/**
 * Type guard to check if a value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}
